import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import { isEmpty, capitalize, isString } from 'lodash'
import PropTypes from 'prop-types'
import React, { useCallback, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'

import { useUpdateReceivesPrintedCopyMutation } from 'api/accounts/updateReceivesPrintedCopy'
import { useUpdateTaxExemptMutation } from 'api/accounts/updateTaxExempt'
import HHAccordion from 'components/common/HHAccordion'
import HHConfirmDialog from 'components/common/HHConfirmDialog'
import HHDisplayMoney from 'components/common/HHDisplayMoney'
import HHSectionPlaceholder from 'components/common/HHSectionPlaceholder'
import BillingDetailsListItem from 'components/customer-details/content/billing/BillingDetailsListItem'
import { hasBillingEmail } from 'components/customer-details/content/common/no-billing-email-dialog/hasBillingEmail'
import NoBillingEmailDialog from 'components/customer-details/content/common/no-billing-email-dialog/NoBillingEmailDialog'
import { getCustomerBillingDetails, isTaxExempt } from 'data/customer/billingDetailsSelector'
import { canUpdateCustomerBillingProfiles } from 'data/permissions/permissionsSelectors'
import { getCustomerDetails } from 'middleware/actions/customers'
import T from 'T'
import { handleError } from 'utils/error'
import { get } from 'utils/lodash'
import { memo } from 'utils/react'

import BillingDetailsActionMenu from './BillingDetailsActionMenu'
import BillingField from './BillingField'

const BillingDetails = ({ customerDetails = {} }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const dispatch = useDispatch()
  const canUpdateBilling = useSelector(canUpdateCustomerBillingProfiles, shallowEqual)
  const [isConfirmingTaxRemoval, setIsConfirmingTaxRemoval] = useState(false)
  const [isMissingBillingEmail, setIsMissingBillingEmail] = useState(false)
  const [updateReceivesPrintedCopy] = useUpdateReceivesPrintedCopyMutation()
  const [updateTaxExempt] = useUpdateTaxExemptMutation()
  const accountId = get(customerDetails, 'id')
  const receivesPrintedCopy = get(customerDetails, 'accountBilling.receivesPrintedCopy', false)
  const canEmailInvoices = hasBillingEmail(get(customerDetails, 'contacts', []))
  // Not utils get: it turns '' into the default, and '' is exempt with no identifier on file.
  const taxExemptOnFile = customerDetails?.accountBilling?.taxExempt ?? null
  const isExempt = Boolean(isTaxExempt(taxExemptOnFile))
  const taxIdentifier = isString(taxExemptOnFile) ? taxExemptOnFile.trim() : ''
  const creditLimitCents = get(customerDetails, 'creditLimitCents', 0)
  const billingProfile = get(customerDetails, 'accountBilling.billingProfile', {})
  const isBillingProfileEmpty = isEmpty(billingProfile)

  const { profileName, billingCycle, paymentType, timing, firstMonths, balance, paymentTerms } = getCustomerBillingDetails(customerDetails)

  const handleActionClick = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseMenu = () => {
    setAnchorEl(null)
  }

  // The panel reads the legacy customers slice, which no RTK Query tag can invalidate.
  const handleReceivesPrintedCopyChange = useCallback(
    event => {
      if (!event.target.checked && !canEmailInvoices) {
        setIsMissingBillingEmail(true)
        return
      }
      updateReceivesPrintedCopy({ accountId, receivesPrintedCopy: event.target.checked })
        .unwrap()
        .then(() => dispatch(getCustomerDetails({ accountId })))
        .catch(handleError)
    },
    [accountId, canEmailInvoices, dispatch, updateReceivesPrintedCopy]
  )

  const handleCloseMissingBillingEmail = useCallback(() => setIsMissingBillingEmail(false), [])

  const writeTaxExempt = useCallback(
    taxExempt =>
      updateTaxExempt({ accountId, taxExempt })
        .unwrap()
        .then(() => dispatch(getCustomerDetails({ accountId })))
        .catch(handleError),
    [accountId, dispatch, updateTaxExempt]
  )

  // Off writes null, so an identifier on file would be gone for good: ask before discarding it.
  const handleTaxExemptChange = useCallback(
    event => {
      if (!event.target.checked && taxIdentifier) {
        setIsConfirmingTaxRemoval(true)
        return
      }
      writeTaxExempt(event.target.checked)
    },
    [taxIdentifier, writeTaxExempt]
  )

  const handleKeepTaxExemption = useCallback(() => setIsConfirmingTaxRemoval(false), [])

  const handleRemoveTaxExemption = useCallback(() => {
    setIsConfirmingTaxRemoval(false)
    writeTaxExempt(false)
  }, [writeTaxExempt])

  return (
    <>
      <HHAccordion
        isExpandCollapseHeightSame
        isExpanded
        actionType="ellipsis"
        withNewColors
        summary={T.BILLING_DETAILS}
        onActionClick={handleActionClick}
      >
        {isBillingProfileEmpty && <HHSectionPlaceholder title={T.NO_BILLING_DETAILS} />}
        {!isBillingProfileEmpty && (
          <Box width="100%" p={2}>
            <Grid container sx={{ mt: 1 }}>
              <>
                <Grid size="grow">
                  <BillingDetailsListItem
                    primaryTypographyProps={{
                      fontWeight: 600,
                      variant: 'h4',
                    }}
                    primary={<HHDisplayMoney value={balance} />}
                    secondary={T.BALANCE}
                  />
                </Grid>
              </>
              {creditLimitCents > 0 && (
                <>
                  <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

                  <Grid size="grow">
                    <BillingDetailsListItem
                      primary={<HHDisplayMoney value={creditLimitCents} formatToDollars />}
                      secondary={T.CREDIT_LIMIT}
                      primaryTypographyProps={{
                        textAlign: 'right',
                        variant: 'h5',
                      }}
                      secondaryTypographyProps={{
                        textAlign: 'right',
                      }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
            <Divider sx={{ my: 1 }} />

            <BillingDetailsListItem primary={profileName} secondary={T.BILLING_PROFILE} />
            <BillingDetailsListItem primary={paymentType} secondary={T.TYPE} />
            <Grid container flexWrap="nowrap">
              <Grid size="grow">
                <BillingDetailsListItem primary={timing} secondary={T.TIMING} />
              </Grid>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Grid size="grow">
                <BillingDetailsListItem
                  primary={billingCycle}
                  primaryTypographyProps={{
                    textAlign: 'right',
                  }}
                  secondaryTypographyProps={{
                    textAlign: 'right',
                  }}
                  secondary={T.CYCLE}
                />
              </Grid>
            </Grid>
            {firstMonths && <BillingDetailsListItem primary={firstMonths} secondary={T.FIRST_MONTHS} />}
            <BillingDetailsListItem secondary={T.PAYMENT_TERMS} primary={paymentTerms} />
            <Divider sx={{ my: 1 }} />
            <BillingField
              label={capitalize(T.TAX_EXEMPT)}
              mb={taxIdentifier ? 0 : 1}
              value={
                <Switch
                  checked={isExempt}
                  disabled={!canUpdateBilling}
                  inputProps={{ 'aria-label': capitalize(T.TAX_EXEMPT) }}
                  size="small"
                  onChange={handleTaxExemptChange}
                />
              }
            />
            {taxIdentifier && (
              <Typography color="text.secondary" component="p" mb={1} variant="caption">
                {`${T.TAX_IDENTIFIER}: ${taxIdentifier}`}
              </Typography>
            )}
            <BillingField
              label={T.RECEIVES_PRINTED_COPY}
              mb={0}
              tooltip={T.RECEIVES_PRINTED_COPY_TOOLTIP}
              value={
                <Switch
                  checked={receivesPrintedCopy}
                  disabled={!canUpdateBilling}
                  inputProps={{ 'aria-label': T.RECEIVES_PRINTED_COPY }}
                  size="small"
                  onChange={handleReceivesPrintedCopyChange}
                />
              }
            />
          </Box>
        )}
      </HHAccordion>
      <BillingDetailsActionMenu anchorEl={anchorEl} handleCloseMenu={handleCloseMenu} customerDetails={customerDetails} />
      <HHConfirmDialog
        confirmButtonTitle={T.REMOVE_EXEMPTION}
        confirmDescription={
          <>
            {T.REMOVE_TAX_EXEMPTION_MESSAGE}
            <strong>{taxIdentifier}</strong>
            <Typography color="text.secondary" component="span" display="block" mt={2} variant="body2">
              {T.REMOVE_TAX_EXEMPTION_RESTORE_HINT}
            </Typography>
          </>
        }
        confirmTitle={T.REMOVE_TAX_EXEMPTION}
        isOpen={isConfirmingTaxRemoval}
        onClose={handleKeepTaxExemption}
        onConfirm={handleRemoveTaxExemption}
      />
      <NoBillingEmailDialog isOpen={isMissingBillingEmail} onClose={handleCloseMissingBillingEmail} />
    </>
  )
}

BillingDetails.propTypes = {
  customerDetails: PropTypes.object,
}

export default memo(BillingDetails)
